import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';
import SettingsIcon from '@smart-tv/components/icons/settings';
import { SettingsNotificationIcon } from '@smart-tv/components/icons/settings-notification';
import { navigationTestIdMap } from '@smart-tv/e2e/shared/variables';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useCollections from '@smart-tv/hooks/use-collections';
import useDialog from '@smart-tv/hooks/use-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';

import {
  HEADER_EXPLORE,
  HEADER_HOME,
  HEADER_SEARCH,
  HEADER_SETTINGS,
  HEADER_TVGUIDE,
  HEADER_DEVELOPER,
  isTvGuideDateReset
} from './header-helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import { featureFlagsAtom } from '@smart-tv/authenticated-app';
import { breadcrumbUrlContainsPathname } from '../player/_utils/navigation-utils';
import {
  headerActiveAtom,
  networkLogEnabledAtom,
  skipAutoFocusOnFrontpageAtom,
  topLevelPageReadyAtom
} from '@smart-tv/atoms';
import closeTvApp from '@smart-tv/utils/closeTvApp';
import styles from './header.module.scss';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';

// MRA - Developer settings
import { openNetworkLog } from '@smart-tv/components/network-log-overlay/network-log-overlay';
import NorlysCircle from '@smart-tv/components/icons/norlys-circle';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
// end

export interface LinkContent {
  title: string;
  href: string;
  focusKey: string;
  icon?: React.ReactNode;
  staticText?: boolean;
}

export const links: LinkContent[] = [
  {
    title: 'Søg',
    href: '/search',
    focusKey: HEADER_SEARCH
  },
  {
    title: 'Hjem',
    href: '/',
    focusKey: HEADER_HOME
  },
  {
    title: 'TV-guide',
    href: '/tv-guide',
    focusKey: HEADER_TVGUIDE
  },
  {
    title: 'Udforsk',
    href: '/udforsk',
    focusKey: HEADER_EXPLORE
  }
];

export const controls: LinkContent[] = [
  {
    title: 'Indstillinger',
    href: '/settings',
    focusKey: HEADER_SETTINGS,
    icon: <SettingsIcon color="inherit" size="xl" />,
    staticText: false
  },
  ...(process.env.NODE_ENV === 'development'
    ? [
        {
          title: 'Udvikler',
          href: '/developer',
          focusKey: HEADER_DEVELOPER,
          icon: <NorlysCircle style={{ color: 'inherit' }} />,
          staticText: false
        }
      ]
    : [])
];

// Derived from the array above instead of hardcoded, since its position can change (e.g. Søg).
const HOME_INDEX = links.findIndex((link) => link.focusKey === HEADER_HOME);

export const useNavigateToHeaderDestination = () => {
  const navigate = useNavigate();

  return (headerIndex: number) => {
    const destination = [...links, ...controls][headerIndex];
    if (destination) navigate(destination.href);
  };
};

interface HeaderNavButtonProps {
  title: string;
  href: string;
  focusKey: string;
  icon?: React.ReactNode;
  staticText?: boolean;
  index: number;
  active?: number;
  hasNpvrNotification: boolean;
  onButtonClick: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    href: string,
    index: number,
    focusKey: string
  ) => void;
  onNavFocus: (index: number, href: string, focusKey: string) => void;
  onArrowPress: (direction: string) => boolean;
}

// Hoisted to module scope so it keeps a stable component identity across HeaderNavigation renders.
const HeaderNavButton: React.FC<HeaderNavButtonProps> = ({
  title,
  href,
  focusKey,
  icon,
  staticText,
  index,
  active,
  hasNpvrNotification,
  onButtonClick,
  onNavFocus,
  onArrowPress
}) => {
  const featureFlags = useAtomValue(featureFlagsAtom);

  if (
    (focusKey === HEADER_EXPLORE && !featureFlags?.showUniverses) ||
    (focusKey === HEADER_SEARCH && !featureFlags?.showSearch)
  ) {
    return null;
  }

  return (
    <Button
      onClick={(event) => onButtonClick(event, href, index, focusKey)}
      onNavFocus={() => onNavFocus(index, href, focusKey)}
      onArrowPress={onArrowPress}
      selected={active === index}
      focusKey={focusKey}
      variant="header"
      data-testid={navigationTestIdMap[title.replace('-', '').toLowerCase()]}
      icon={
        focusKey === HEADER_SETTINGS && hasNpvrNotification ? (
          <SettingsNotificationIcon size="xl" />
        ) : (
          icon
        )
      }
      staticText={staticText}
      expandOnFocus={true}
    >
      {title}
    </Button>
  );
};

interface Props {
  onClick: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    href: string,
    index: number,
    focusKey: string
  ) => void;
  active?: number;
}
const findPreferredChildFocusKey = (active: number | undefined) => {
  if (active === undefined) return HEADER_HOME;
  if (active < links.length) {
    return links[active]?.focusKey ? links[active]?.focusKey : HEADER_HOME;
  } else
    return controls[active - links.length]?.focusKey
      ? controls[active - links.length]?.focusKey
      : HEADER_HOME;
};

export const HeaderNavigation: React.FC<Props> = (props: Props) => {
  const { onClick, active } = props;
  // The header unmounts while the player is open, which wipes the library's last-focused child.
  const preferredChildFocusKey = findPreferredChildFocusKey(active);
  const { ref, focusKey, focusSelf, hasFocusedChild } = useFocusable({
    trackChildren: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['up'],
    preferredChildFocusKey
  });
  const navigate = useNavigate();
  const setHeaderActive = useSetAtom(headerActiveAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const setSkipAutoFocusOnFrontpage = useSetAtom(skipAutoFocusOnFrontpageAtom);
  const networkLogEnabled = useAtomValue(networkLogEnabledAtom);
  const topLevelPageReady = useAtomValue(topLevelPageReadyAtom);
  const navigationTimerRef = React.useRef<number>();
  const mountedPageReadyRef = React.useRef(true);
  const latestPreviewHrefRef = React.useRef<string | null>(null);
  const { openCollections } = useCollections();
  const { opened: openDialog } = useDialog();
  const { hasAnyNotification: hasNpvrNotification } = useNpvrNotifications();
  const { checkAndUpdateNetwork } = useNetwork();
  const { selectedDate } = React.useContext(TvGuideContext);

  const handleOnClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      href: string,
      index: number,
      focusKey: string
    ) => {
      onClick(event, href, index, focusKey);
    },
    [onClick]
  );

  const handleNavigationFocus = React.useCallback(
    (index: number, href: string, focusKey: string) => {
      if (IS_BROWSER) return;

      latestPreviewHrefRef.current = href;

      const wasAlreadyActiveTab = active === index;
      setHeaderActive(index);

      const hadPendingPreview = navigationTimerRef.current !== undefined;
      if (hadPendingPreview) {
        window.clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = undefined;
      }

      const isResettingTvGuideDate =
        !wasAlreadyActiveTab && isTvGuideDateReset(focusKey, href, selectedDate.isToday);

      if (window.location.pathname === href && !isResettingTvGuideDate) {
        if (hadPendingPreview) setTopLevelPageReady(mountedPageReadyRef.current);
        return;
      }

      if (!hadPendingPreview) {
        mountedPageReadyRef.current = topLevelPageReady;
      }

      setTopLevelPageReady(false);

      navigationTimerRef.current = window.setTimeout(async () => {
        navigationTimerRef.current = undefined;

        const shouldNavigate = window.location.pathname !== href || isResettingTvGuideDate;
        if (!shouldNavigate) return;

        const hasNetwork = await checkAndUpdateNetwork();
        if (!hasNetwork && latestPreviewHrefRef.current === href) {
          setTopLevelPageReady(mountedPageReadyRef.current);
        }
        if (hasNetwork && latestPreviewHrefRef.current === href) {
          if (mountedPageReadyRef.current) {
            React.startTransition(() => {
              navigate(href, { state: { headerPreview: true } });
            });
          } else {
            navigate(href, { state: { headerPreview: true } });
          }
        }
      }, 500);
    },
    [
      navigate,
      setHeaderActive,
      setTopLevelPageReady,
      topLevelPageReady,
      checkAndUpdateNetwork,
      selectedDate.isToday,
      active
    ]
  );

  const handleNavigationArrowPress = React.useCallback(
    (direction: string) => {
      if (!IS_BROWSER && direction === 'down' && !topLevelPageReady) return false;
      return true;
    },
    [topLevelPageReady]
  );

  // Blocks navigating off the left/top edge from whichever button is actually first.
  const handleFirstItemArrowPress = React.useCallback(
    (direction: string) => {
      if (!IS_BROWSER && (direction === 'down' || direction === 'left') && !topLevelPageReady) {
        return false;
      }
      return true;
    },
    [topLevelPageReady]
  );

  const handleDeveloperArrowPress = React.useCallback(
    (direction: string) => {
      if (!IS_BROWSER && direction === 'down' && !topLevelPageReady) return false;
      if (direction === 'up' && networkLogEnabled) {
        openNetworkLog(HEADER_DEVELOPER);
        return false;
      }
      return true;
    },
    [topLevelPageReady, networkLogEnabled]
  );

  const getArrowPressHandler = React.useCallback(
    (buttonFocusKey: string, buttonIndex: number) => {
      if (buttonIndex === 0) return handleFirstItemArrowPress;
      if (buttonFocusKey === HEADER_DEVELOPER) return handleDeveloperArrowPress;
      return handleNavigationArrowPress;
    },
    [handleFirstItemArrowPress, handleDeveloperArrowPress, handleNavigationArrowPress]
  );

  React.useEffect(
    () => () => {
      if (navigationTimerRef.current !== undefined) {
        window.clearTimeout(navigationTimerRef.current);
      }
    },
    []
  );

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (!hasFocusedChild) return;

      if (
        isBackKey(event) &&
        (active !== HOME_INDEX || window.location.href.includes('provideruniverse'))
      ) {
        setHeaderActive(HOME_INDEX);
        setSkipAutoFocusOnFrontpage(true);
        setFocus(HEADER_HOME);
        navigate('/');
      } else if (isBackKey(event)) {
        closeTvApp();
      }
    },
    [hasFocusedChild, active, setHeaderActive, setSkipAutoFocusOnFrontpage, navigate]
  );

  React.useEffect(() => {
    const navigatedFromUniverse = breadcrumbUrlContainsPathname('universe');
    const hasOpenProgramSheet = new URLSearchParams(window.location.search).has('programsheet');
    if (
      window.location.pathname !== '/' &&
      !navigatedFromUniverse &&
      !hasOpenProgramSheet &&
      !openCollections &&
      !openDialog
    ) {
      focusSelf();
    }
  }, [focusSelf]);

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className={`${styles['header-navigation']} ${styles['header__50']}`}>
        {links.map(({ title, href, focusKey: linkFocusKey, icon, staticText }, index) => (
          <HeaderNavButton
            key={linkFocusKey}
            title={title}
            href={href}
            focusKey={linkFocusKey}
            icon={icon}
            staticText={staticText}
            index={index}
            active={active}
            hasNpvrNotification={hasNpvrNotification}
            onButtonClick={handleOnClick}
            onNavFocus={handleNavigationFocus}
            onArrowPress={getArrowPressHandler(linkFocusKey, index)}
          />
        ))}
      </div>
      <div className={`${styles['header-controls']} ${styles['header__25']}`}>
        {controls.map(({ title, href, focusKey: controlFocusKey, icon, staticText }, index) => (
          <HeaderNavButton
            key={controlFocusKey}
            title={title}
            href={href}
            focusKey={controlFocusKey}
            icon={icon}
            staticText={staticText}
            index={links.length + index}
            active={active}
            hasNpvrNotification={hasNpvrNotification}
            onButtonClick={handleOnClick}
            onNavFocus={handleNavigationFocus}
            onArrowPress={getArrowPressHandler(controlFocusKey, links.length + index)}
          />
        ))}
      </div>
    </FocusContext.Provider>
  );
};

export default React.memo(HeaderNavigation);
